/**
 * Embed Consent Sync
 *
 * Bridges Usercentrics consent state with the embed consent DOM handler.
 * Maps UC service IDs to social embed platforms and syncs consent changes
 * to show/hide embed content and overlays.
 *
 * DOM functions (loadEmbedConsentDOM, revokeEmbedConsentDOM) are injected
 * via initEmbedConsentSync to avoid a circular dependency with embedConsentHandler.
 */

import type { SocialPlatform } from "~/components/renderers/RichTextRenderer";
import type { ConsentDetails } from "~/types/consent";
import { consentStore, consentActions, onConsentChange } from "~/stores/consent";
import { logger } from "~/lib/logger/logger";

/**
 * Maps Usercentrics service IDs to social embed platform identifiers.
 *
 * To add a new platform: find the service ID in UC admin panel or via
 * `window.__ucCmp.getConsentDetails()` → services → [id], then add it here.
 */
const UC_SERVICE_TO_EMBED_PLATFORM: Record<string, SocialPlatform> = {
  "BJz7qNsdj-7": "youtube", // "YouTube Video"
  ko1w5PpFl: "facebook", // "Facebook Pixel"
  // NOTE: The following platforms need Usercentrics service IDs before per-platform
  // consent can work. Find them in the UC admin panel → Service Settings → Details,
  // or via `window.__ucCmp.getConsentDetails()` → services → [id] in the browser console.
  // Until mapped, these platforms fall back to the ALL_ACCEPTED check in syncEmbedConsent(),
  // meaning embeds only load when the user accepts ALL services via the UC banner.
  // Granular per-platform consent (via the embed overlay buttons) will not persist for these.
  //
  // "SERVICE_ID": "instagram",
  // "SERVICE_ID": "linkedin",
  // "SERVICE_ID": "x",
  // "SERVICE_ID": "tiktok",
  // "SERVICE_ID": "spotify",
};

/**
 * All embed platforms that need consent handling.
 *
 * Platforms without a UC service ID yet still need to be listed so that
 * the ALL_ACCEPTED fallback can auto-load them when the user accepts all.
 */
const ALL_EMBED_PLATFORMS: SocialPlatform[] = [
  "youtube",
  "facebook",
  "instagram",
  "x",
  "tiktok",
  "spotify",
  "linkedin",
];

/**
 * Reverse mapping: embed platform → Usercentrics service ID.
 * Only contains platforms that have a UC service ID configured.
 */
const EMBED_PLATFORM_TO_UC_SERVICE: Partial<Record<SocialPlatform, string>> = Object.fromEntries(
  Object.entries(UC_SERVICE_TO_EMBED_PLATFORM).map(([ucId, platform]) => [platform, ucId])
);

/**
 * Checks whether Usercentrics consent is currently given for a social embed platform.
 * Used by the embed consent handler for initial DOM sync.
 */
export function hasEmbedConsent(platform: SocialPlatform): boolean {
  const details = consentStore.consentDetails();
  if (!details) return false;

  const allAccepted = details.consent.status === "ALL_ACCEPTED";
  const serviceId = EMBED_PLATFORM_TO_UC_SERVICE[platform];
  const service = serviceId ? details.services[serviceId] : undefined;

  // If the platform isn't registered in UC but user accepted all, treat as consented
  if (allAccepted && !service) return true;

  return service?.consent?.given ?? false;
}

/**
 * Accepts a social embed platform in Usercentrics.
 * Looks up the service by ID, then updates + saves consent.
 */
export async function giveEmbedConsent(platform: SocialPlatform): Promise<void> {
  const serviceId = EMBED_PLATFORM_TO_UC_SERVICE[platform];
  if (!serviceId) {
    logger.warn(
      `[EmbedConsent] No UC service ID mapped for ${platform} — consent will not persist across sessions`
    );
    return;
  }

  const details = consentStore.consentDetails();
  if (!details) {
    logger.warn(`[EmbedConsent] UC not initialized yet for ${platform} — consent will not persist`);
    return;
  }

  const service = details.services[serviceId];
  if (!service) {
    logger.warn(
      `[EmbedConsent] UC service ${serviceId} not found for ${platform} — consent will not persist`
    );
    return;
  }

  logger.consent(`Accepting embed consent for ${platform} (UC service ID: ${serviceId})`);
  await consentActions.updateServices([{ id: serviceId, consent: true }]);
  await consentActions.saveConsents();
}

interface EmbedDOMHandlers {
  load: (platform: SocialPlatform) => void;
  revoke: (platform: SocialPlatform) => void;
}

/**
 * Registers the embed consent sync as a consent change subscriber.
 * DOM handlers are injected to avoid circular dependency with embedConsentHandler.
 * Returns an unsubscribe function for cleanup.
 */
export function initEmbedConsentSync(dom: EmbedDOMHandlers): () => void {
  function syncEmbedConsent(details: ConsentDetails): void {
    const allAccepted = details.consent.status === "ALL_ACCEPTED";

    for (const platform of ALL_EMBED_PLATFORMS) {
      const serviceId = EMBED_PLATFORM_TO_UC_SERVICE[platform];
      const service = serviceId ? details.services[serviceId] : undefined;

      if (allAccepted && !service) {
        // Platform not registered in UC, but user accepted everything — load it
        dom.load(platform);
        continue;
      }

      if (!service?.consent) continue;

      if (service.consent.given) {
        dom.load(platform);
      } else {
        dom.revoke(platform);
      }
    }
  }

  return onConsentChange(syncEmbedConsent);
}
